import utils from "../../../utils/utils";

/**
 * Review due date = published date + ReviewDateNumber × (Month | Year).
 * Matches publish flow suffix values in legacy DMS.
 */
export function calculateReviewDueDate(
  publishedDateTime,
  reviewDateNumber,
  reviewDateSuffix,
) {
  const base = utils.parseDateInput(publishedDateTime);
  if (!base) return null;

  const amount = Number(reviewDateNumber);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const suffix = String(reviewDateSuffix || "")
    .trim()
    .toLowerCase();
  if (!suffix) return null;

  const due = new Date(base.getTime());
  if (suffix.startsWith("year")) {
    due.setFullYear(due.getFullYear() + amount);
    return due;
  }
  if (suffix.startsWith("month")) {
    due.setMonth(due.getMonth() + amount);
    return due;
  }
  return null;
}

export function formatReviewDurationLabel(reviewDateNumber, reviewDateSuffix) {
  if (reviewDateNumber == null || !String(reviewDateSuffix || "").trim()) {
    return "";
  }
  const suffix = String(reviewDateSuffix)
    .trim()
    .replace(/^every\s+/i, "");
  return `${reviewDateNumber} ${suffix}`;
}

/** Prefer API ReviewDueDate; fall back to client calculation from publish + duration. */
export function resolveReviewDueDate(row) {
  const fromApi = utils.parseDateInput(row?.ReviewDueDate);
  if (fromApi) return fromApi;
  return calculateReviewDueDate(
    row?.PublishedDateTime,
    row?.ReviewDateNumber,
    row?.ReviewDateSuffix,
  );
}

const DUE_SOON_DAYS = 30;

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getReviewDateStatus(row) {
  const duration = formatReviewDurationLabel(
    row?.ReviewDateNumber,
    row?.ReviewDateSuffix,
  );
  const dueDate = resolveReviewDueDate(row);

  if (!dueDate) {
    const fallback = row?.ReviewDateLabel || row?.ReviewDate || "";
    const primary =
      duration ||
      (!fallback || fallback === "N/A" ? "" : String(fallback));
    return {
      duration: primary || "—",
      dueDateLabel: "",
      status: "none",
      badge: "",
    };
  }

  const today = startOfDay(new Date());
  const due = startOfDay(dueDate);
  const diffDays = Math.round((due - today) / 86400000);

  let status = "ok";
  let badge = `Due in ${diffDays} day(s)`;

  if (diffDays < 0) {
    status = "overdue";
    badge = `${Math.abs(diffDays)} day(s) overdue`;
  } else if (diffDays === 0) {
    status = "due-today";
    badge = "Due today";
  } else if (diffDays <= DUE_SOON_DAYS) {
    status = "due-soon";
    badge = `Due in ${diffDays} day(s)`;
  }

  return {
    duration: duration || "—",
    dueDateLabel: utils.formatDateTimeDisplay(dueDate).split(" ")[0],
    status,
    badge,
  };
}

function isLearningRowCompleted(row) {
  if (String(row?.LearningStatus || "").trim() === "Completed") return true;
  const acknowledged = Number(row?.IsAcknowledged) === 1 || row?.IsAcknowledged === true;
  const quizRequired = Number(row?.IsQuizApplicable) === 1 || row?.IsQuizApplicable === true;
  const passed = Number(row?.IsPassed) === 1 || row?.IsPassed === true;
  return acknowledged && (!quizRequired || passed);
}

export function formatReviewDateCell(row) {
  const review = getReviewDateStatus(row);
  const status = isLearningRowCompleted(row) ? "completed" : review.status;
  return {
    primary: review.duration,
    secondary: review.dueDateLabel,
    status,
    badge: review.badge,
  };
}

export function formatReviewDateDisplay(row) {
  const { primary, secondary } = formatReviewDateCell(row);
  if (secondary) return `${primary} (${secondary})`;
  return primary;
}
